import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, Clock, Loader2, Lock, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import PersonalDataFields, {
    PersonalDataErrors,
    PersonalDataField,
    PersonalDataValues,
    validatePersonalData,
} from '../../../components/auth/PersonalDataFields';
import { CardFields, useEpaycoCardTokenizer } from '../../payments/components/recurring/CardFields';
import { PrepagoPlan, formatCOP, planById, planFromUrlCode, planUrlCode } from '../../payments/plans';
import { recurringPaymentsApi } from '../../../services/recurringPaymentsApi';
import { clearEntryCode, getEntryPlan } from '../../../lib/signupEntry';
import { errorCode, userErrorMessage } from '../../../lib/userErrors';
import { reviewPendingPayment, usePaymentReviewPoll } from '../../payments/usePaymentReviewPoll';
import { paidSignupApi, PlanPrices } from '../paidSignupApi';
import { usePaidSignupPrices } from '../hooks/usePaidSignupPrices';
import { InlineError, LogoutLink, SignupShell } from '../components/SignupShell';
import { PlanSummary } from '../components/PlanSummary';
import { linkButtonClass, primaryButtonClass } from '../constants';

const PAYMENT_PENDING = 'payment_pending';

/** The bank is still reviewing the first charge; the ePayco webhook settles it. */
const PendingReview: React.FC<{
    planName: string;
    onActivated: () => void;
    onDeclined: (message: string) => void;
}> = ({ planName, onActivated, onDeclined }) => {
    const { refreshUser } = useAuth();
    const [checking, setChecking] = useState(false);

    const finish = async (outcome: Awaited<ReturnType<typeof reviewPendingPayment>>) => {
        if (outcome === 'pending') {
            toast.info('Tu pago sigue en revisión', { description: 'Vuelve a intentarlo en unos minutos.' });
            return;
        }
        await refreshUser();
        if (outcome === 'active') {
            clearEntryCode();
            toast.success('¡Pago aprobado!', { description: 'Tu plan está activo.' });
            onActivated();
            return;
        }
        onDeclined(outcome.declined);
    };

    usePaymentReviewPoll(true, () => { void finish('active'); }, message => { void finish({ declined: message }); });

    const recheck = async () => {
        setChecking(true);
        try {
            await finish(await reviewPendingPayment());
        } catch (err) {
            console.error(err);
            toast.error(userErrorMessage(err, 'No pudimos revisar el estado del pago. Intenta de nuevo.'));
        } finally {
            setChecking(false);
        }
    };

    return (
        <SignupShell title="Tu pago está en revisión" subtitle="Tu banco está confirmando el cobro">
            <div className="space-y-5 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 text-amber-500 flex items-center justify-center">
                    <Clock className="w-7 h-7" />
                </div>
                <p className="text-sm text-gray-600">
                    El plan {planName} puede tardar unos minutos. No vuelvas a pagar: cuando tu banco lo apruebe,
                    tu plan se activa y puedes continuar desde esta misma página.
                </p>
                <button type="button" onClick={recheck} disabled={checking} className={primaryButtonClass}>
                    {checking ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Revisar de nuevo'}
                </button>
            </div>
            <LogoutLink disabled={checking} />
        </SignupShell>
    );
};

export const PaymentStep: React.FC<{
    entryCode: string | undefined;
    onExistingAccount: (email: string) => void;
    onPaid: () => void;
}> = ({ entryCode, onExistingAccount, onPaid }) => {
    const { user, startSession, refreshUser } = useAuth();
    const { ready, loadingKey, initError, tokenize, fields } = useEpaycoCardTokenizer();
    const { setPhone: setCardholderPhone, setEmail: setCardholderEmail } = fields;
    const { prices, error: pricesError } = usePaidSignupPrices();
    const [intlPrices, setIntlPrices] = useState<PlanPrices | null>(null);
    const [intlCurrency, setIntlCurrency] = useState<string | null>(null);
    const [phoneCountry, setPhoneCountry] = useState('CO');
    const [regionReady, setRegionReady] = useState(false);
    const countryOverride = new URLSearchParams(window.location.search).get('country') || undefined;
    const chargeUsd = intlPrices !== null;

    useEffect(() => {
        let cancelled = false;
        paidSignupApi.getRegion(countryOverride)
            .then(region => {
                if (cancelled || !region.is_international) return;
                setIntlPrices(region.plans);
                setIntlCurrency(region.currency || 'USD');
                setPhoneCountry((region.country_code || 'CO').toUpperCase());
            })
            .catch(err => console.error(err))
            .finally(() => {
                if (!cancelled) setRegionReady(true);
            });
        return () => {
            cancelled = true;
        };
    }, [countryOverride]);

    const shownPrices = chargeUsd ? intlPrices : prices;
    const formatMoney = (value: number) => chargeUsd
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: intlCurrency || 'USD' }).format(value)
        : formatCOP(value);
    const formRef = useRef<HTMLFormElement>(null);

    const isNewSignup = !user;
    const [plan, setPlan] = useState<PrepagoPlan>(
        () => planById(user?.signup_plan) ?? planFromUrlCode(getEntryPlan())
    );
    const [personal, setPersonal] = useState<PersonalDataValues>({
        email: '', confirm_email: '', first_name: '', last_name: '', phone: '',
    });
    const [personalErrors, setPersonalErrors] = useState<PersonalDataErrors>({});
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pendingReview, setPendingReview] = useState(false);
    const [useRegistrationContact, setUseRegistrationContact] = useState(false);

    const userId = user?.id;
    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        recurringPaymentsApi.getSubscription({ force: true })
            .then(({ subscription }) => {
                if (!cancelled && subscription?.status === 'pending_first_payment') setPendingReview(true);
            })
            .catch(err => console.error(err));
        return () => {
            cancelled = true;
        };
    }, [userId]);

    const price = shownPrices?.[plan.id];

    const applyRegistrationContact = (checked: boolean) => {
        setUseRegistrationContact(checked);
        if (!checked) return;
        const account = user as { email?: string; phone?: string } | null;
        const email = (isNewSignup ? personal.email : account?.email || '').trim();
        const phone = isNewSignup ? personal.phone : account?.phone || '';
        if (email) setCardholderEmail(email);
        if (phone) setCardholderPhone(phone);
    };

    const handlePersonalChange = (field: PersonalDataField, value: string) => {
        setPersonal(prev => ({ ...prev, [field]: value }));
        if (personalErrors[field]) setPersonalErrors(prev => ({ ...prev, [field]: undefined }));
    };

    const handlePay = async (e: React.FormEvent) => {
        e.preventDefault();
        if (processing || !formRef.current || price === undefined) return;
        setError(null);

        if (isNewSignup) {
            const errors = validatePersonalData(personal, { withEmailConfirmation: true });
            setPersonalErrors(errors);
            if (Object.keys(errors).length > 0) return;
            if (!entryCode) {
                setError('Este enlace de registro no es válido o expiró. Vuelve a abrirlo desde nuestra página.');
                return;
            }
        }

        setProcessing(true);
        try {
            if (isNewSignup) {
                try {
                    const session = await paidSignupApi.register({
                        email: personal.email.trim().toLowerCase(),
                        first_name: personal.first_name.trim(),
                        last_name: personal.last_name.trim(),
                        phone: personal.phone,
                        entry_code: entryCode!,
                        plan: planUrlCode(plan.id),
                    });
                    startSession(session.access_token, session.user);
                } catch (err: any) {
                    if (err?.response?.status === 409) {
                        onExistingAccount(personal.email.trim().toLowerCase());
                        return;
                    }
                    throw err;
                }
            }

            const card = await tokenize(formRef.current);
            const method = await recurringPaymentsApi.registerCard({ ...card, set_as_default: true });
            try {
                if (chargeUsd) {
                    await recurringPaymentsApi.createInternationalSubscription({
                        plan_type: plan.id,
                        payment_method_id: method.id,
                    }, countryOverride);
                } else {
                    await recurringPaymentsApi.createSubscription({
                        plan_type: plan.id,
                        payment_method_id: method.id,
                    });
                }
            } catch (chargeErr) {
                if (errorCode(chargeErr) === PAYMENT_PENDING) {
                    // The card belongs to a charge the bank may still approve.
                    setPendingReview(true);
                    return;
                }
                // A declined card must not count toward the saved-cards limit, or retries get blocked.
                await recurringPaymentsApi.deleteCard(method.id).catch(cleanupErr => console.error(cleanupErr));
                throw chargeErr;
            }

            clearEntryCode();
            await refreshUser();
            toast.success('¡Pago aprobado!', { description: `Tu plan ${plan.name} está activo.` });
            onPaid();
        } catch (err: any) {
            console.error(err);
            setError(userErrorMessage(err, 'Tu banco no aprobó el pago. Intenta con otra tarjeta.'));
        } finally {
            setProcessing(false);
        }
    };

    if (pendingReview) {
        return (
            <PendingReview
                planName={plan.name}
                onActivated={onPaid}
                onDeclined={(message) => {
                    setPendingReview(false);
                    setError(message);
                }}
            />
        );
    }

    const greetingName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
    const priceText = price === undefined ? '' : ` ${formatMoney(price)}`;
    const payLabel = isNewSignup
        ? `Pagar${priceText} y crear cuenta`
        : `Pagar${priceText} y activar mi cuenta`;

    return (
        <SignupShell
            title={isNewSignup ? 'Crea tu cuenta' : `Hola ${greetingName}`.trim()}
            subtitle={isNewSignup
                ? 'Completa tus datos y activa tu plan en un solo paso'
                : 'Activa tu plan para empezar a usar Reveca'}
        >
            <form ref={formRef} onSubmit={handlePay} className="space-y-6">
                {isNewSignup && (
                    <section className="space-y-5">
                        <PersonalDataFields
                            values={personal}
                            errors={personalErrors}
                            onChange={handlePersonalChange}
                            disabled={processing}
                            withEmailConfirmation
                            phoneCountry={chargeUsd ? phoneCountry : 'CO'}
                        />
                    </section>
                )}

                <PlanSummary plan={plan} prices={shownPrices} onChange={setPlan} disabled={processing} formatAmount={formatMoney} />

                <section>
                    <h2 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                        <Lock className="w-4 h-4 text-emerald-500" />
                        Datos de la tarjeta
                    </h2>
                    {loadingKey ? (
                        <div className="py-6 flex items-center justify-center gap-2 text-slate-400 text-sm">
                            <Loader2 className="w-5 h-5 animate-spin text-[#1CB3B3]" />
                            Iniciando conexión segura con ePayco...
                        </div>
                    ) : (
                        <>
                            <label className="mb-4 flex items-start gap-2 text-sm text-slate-600">
                                <input
                                    type="checkbox"
                                    checked={useRegistrationContact}
                                    disabled={processing}
                                    onChange={e => applyRegistrationContact(e.target.checked)}
                                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#1CB3B3] focus:ring-[#1CB3B3]"
                                />
                                <span>Usar el correo y el celular de mi registro</span>
                            </label>
                            <CardFields
                                fields={fields}
                                international={chargeUsd}
                                phoneCountry={chargeUsd ? phoneCountry : 'CO'}
                            />
                        </>
                    )}
                </section>

                <InlineError message={error || initError || pricesError} />

                <div className="space-y-3">
                    <button
                        type="submit"
                        disabled={processing || !ready || !regionReady || price === undefined || (!chargeUsd && prices === null)}
                        className={primaryButtonClass}
                    >
                        {processing ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span>Procesando pago...</span>
                            </>
                        ) : (
                            <>
                                <span>{payLabel}</span>
                                <ArrowRight className="h-5 w-5" />
                            </>
                        )}
                    </button>
                    <p className="text-center text-xs text-slate-500 flex items-start justify-center gap-1.5">
                        <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" />
                        <span>
                            Pago seguro procesado por ePayco. Los datos de tu tarjeta viajan cifrados y no se guardan en Reveca.
                            {price !== undefined && <> Se cobrarán {formatMoney(price)} cada mes; puedes cancelar cuando quieras.</>}
                        </span>
                    </p>
                </div>
            </form>

            {isNewSignup ? (
                <p className="text-center text-sm text-gray-600 mt-6">
                    ¿Ya tienes cuenta?{' '}
                    <a href="/login" className={linkButtonClass}>Inicia sesión</a>
                </p>
            ) : (
                <LogoutLink disabled={processing} />
            )}
        </SignupShell>
    );
};
